import { films, categoryMeta, type Film, type CategoryMeta } from "@/mocks/films";
import { specials, type SpecialTopic } from "@/mocks/specials";
import { filmNews, type FilmNews } from "@/mocks/news";

export type { Film, CategoryMeta } from "@/mocks/films";
export type { SpecialTopic } from "@/mocks/specials";
export type { FilmNews } from "@/mocks/news";

export function getAllFilms(): Film[] {
  return films;
}

export function getFilmById(id: string): Film | undefined {
  return films.find((film) => film.id === id);
}

export function getFilmsByIds(ids: string[]): Film[] {
  return ids
    .map((id) => films.find((film) => film.id === id))
    .filter((film): film is Film => Boolean(film));
}

export function getCategoryMeta(key: string): CategoryMeta | undefined {
  return categoryMeta.find((item) => item.key === key);
}

export function getSpecialById(id: string): SpecialTopic | undefined {
  return specials.find((item) => item.id === id);
}

export function getNewsForFilm(filmId: string): FilmNews[] {
  return filmNews.filter((item) => item.filmId === filmId);
}

export function searchFilms(keyword: string): Film[] {
  const query = keyword.trim().toLowerCase();
  if (!query) return films;
  return films.filter((film) => {
    const haystack = [film.title, film.oneLine, film.director, ...film.cast, ...film.genres, ...film.synopsis].join(" ").toLowerCase();
    return haystack.includes(query);
  });
}

export function getRelatedFilms(film: Film, limit = 4): Film[] {
  const scored = films
    .filter((item) => item.id !== film.id)
    .map((item) => {
      let score = 0;
      if (item.category === film.category) score += 2;
      film.genres.forEach((genre) => {
        if (item.genres.includes(genre)) score += 3;
      });
      film.tags.forEach((tag) => {
        if (item.tags.includes(tag)) score += 2;
      });
      if (item.region === film.region) score += 1;
      if (item.director === film.director) score += 4;
      return { item, score };
    })
    .sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map((entry) => entry.item);
}

export function getSpecialsForFilm(filmId: string): SpecialTopic[] {
  return specials.filter((topic) => topic.filmIds.includes(filmId));
}

export function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return `${date.getFullYear()} 年 ${date.getMonth() + 1} 月 ${date.getDate()} 日`;
}

export function getCategoryLabel(category: Film["category"]): string {
  const meta = categoryMeta.find((item) => item.key === category);
  return meta ? meta.name : "影视";
}